<template>
  <div class="warehouse-page">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" @click="backMainPanel" class="logo-button" />
    </div>

    <div class="page-content">
      <div class="page-header">
        <div>
          <h2>Requisiciones</h2>
          <p class="page-subtitle">Solicitudes de piezas desde el taller · {{ totalCount }} requisiciones</p>
        </div>
        <div class="header-actions">
          <button class="action-button" @click="backWarehouse">Volver a Almacén</button>
        </div>
      </div>

      <div class="filters-card">
        <div class="filter-group">
          <label for="filter-rq-folio">Folio / código</label>
          <input id="filter-rq-folio" type="text" v-model="filters.requisition_number" @keyup.enter="applyFilters" placeholder="RQ-1" />
        </div>
        <div class="filter-group">
          <label for="filter-rq-order">Orden</label>
          <input id="filter-rq-order" type="text" v-model="filters.order_number" @keyup.enter="applyFilters" placeholder="Folio de orden" />
        </div>
        <div class="filter-group">
          <label for="filter-rq-part">Pieza</label>
          <input id="filter-rq-part" type="text" v-model="filters.part" @keyup.enter="applyFilters" placeholder="Nombre o código" />
        </div>
        <div class="filter-group">
          <label for="filter-rq-status">Estatus</label>
          <select id="filter-rq-status" v-model="filters.status">
            <option value="">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="partial">Parcial</option>
            <option value="fulfilled">Surtida</option>
          </select>
        </div>
        <div class="filter-group">
          <label for="filter-rq-from">Desde</label>
          <input id="filter-rq-from" type="date" v-model="filters.start_date" />
        </div>
        <div class="filter-group">
          <label for="filter-rq-to">Hasta</label>
          <input id="filter-rq-to" type="date" v-model="filters.end_date" />
        </div>
        <div class="filter-buttons">
          <button class="action-button action-primary" @click="applyFilters">Buscar</button>
          <button class="action-button" @click="clearFilters">Limpiar</button>
        </div>
      </div>

      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th :aria-sort="ariaSort('requisition_number')">
                  <button class="th-sort" @click="toggleSort('requisition_number')">
                    <span>Folio</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'requisition_number' }">{{ sortIcon('requisition_number') }}</span>
                  </button>
                </th>
                <th :aria-sort="ariaSort('created_at')">
                  <button class="th-sort" @click="toggleSort('created_at')">
                    <span>Fecha</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'created_at' }">{{ sortIcon('created_at') }}</span>
                  </button>
                </th>
                <th>Orden</th>
                <th>Unidad</th>
                <th>Elaboró</th>
                <th :aria-sort="ariaSort('status')">
                  <button class="th-sort" @click="toggleSort('status')">
                    <span>Estatus</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'status' }">{{ sortIcon('status') }}</span>
                  </button>
                </th>
                <th>Surtido</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr v-for="n in skeletonRows" :key="`skeleton-${n}`">
                  <td v-for="col in 7" :key="col"><span class="skeleton"></span></td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="requisition in requisitions" :key="requisition.id">
                  <td><span class="barcode-pill" role="button" tabindex="0" @click="openDetail(requisition)" @keyup.enter="openDetail(requisition)">{{ requisition.requisition_number }}</span></td>
                  <td>{{ formatDateTime(requisition.created_at) }}</td>
                  <td :class="{ 'cell-muted': !requisition.maintenance_order_number }">{{ requisition.maintenance_order_number || '—' }}</td>
                  <td :class="{ 'cell-muted': !requisition.unit_short_id }">{{ requisition.unit_short_id || '—' }}</td>
                  <td :class="{ 'cell-muted': !requisition.created_by_name }">{{ requisition.created_by_name || '—' }}</td>
                  <td><span class="status-pill" :class="statusClass(requisition.status)">{{ requisition.status_label }}</span></td>
                  <td>
                    <span class="progress-cell">
                      <span class="progress-cell__label">{{ requisition.items_fulfilled }} / {{ requisition.items_total }}</span>
                      <span class="progress-cell__track">
                        <span
                          class="progress-cell__bar"
                          :class="progressClass(requisition)"
                          :style="{ width: progressPercent(requisition) + '%' }"
                        ></span>
                      </span>
                    </span>
                  </td>
                </tr>
                <tr v-if="requisitions.length === 0">
                  <td colspan="7" class="empty-row">
                    <div class="empty-state">
                      <span class="empty-state__icon">📋</span>
                      <span class="empty-state__title">No se encontraron requisiciones</span>
                      <span class="empty-state__hint">Ajusta los filtros para ampliar la búsqueda.</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="pagination-container">
          <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" aria-label="Página anterior">‹</button>
          <span
            v-for="(pageNumber, index) in pageNumbers"
            :key="`${pageNumber}-${index}`"
            :class="{ active: pageNumber === currentPage, ellipsis: pageNumber === ellipsis }"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </span>
          <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages" aria-label="Página siguiente">›</button>
          <span class="pagination-summary">{{ totalCount }} resultados</span>
        </div>
      </div>
    </div>

    <div v-if="detail" class="modal-overlay" @click.self="detail = null">
      <div class="modal-card modal-card--wide" role="dialog" aria-modal="true" aria-labelledby="rq-detail-title">
        <h3 id="rq-detail-title">Requisición {{ detail.requisition_number }}</h3>
        <div class="modal-body">
          <div class="detail-meta">
            <div>
              <span class="detail-meta__label">Código</span>
              <span class="detail-meta__value">{{ detail.barcode }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Fecha</span>
              <span class="detail-meta__value">{{ formatDateTime(detail.created_at) }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Elaboró</span>
              <span class="detail-meta__value">{{ detail.created_by_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Orden</span>
              <span class="detail-meta__value">{{ detail.maintenance_order_number || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Unidad</span>
              <span class="detail-meta__value">{{ detail.unit_short_id || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Estatus</span>
              <span class="detail-meta__value">
                <span class="status-pill" :class="statusClass(detail.status)">{{ detail.status_label }}</span>
              </span>
            </div>
            <div v-if="detail.comments" class="detail-meta--full">
              <span class="detail-meta__label">Comentarios</span>
              <span class="detail-meta__value">{{ detail.comments }}</span>
            </div>
          </div>

          <h4 class="section-title">Piezas solicitadas</h4>
          <table class="data-table data-table--embedded">
            <thead>
              <tr>
                <th class="col--center">Surtida</th>
                <th>Código</th>
                <th>Pieza</th>
                <th class="col--num">Pedida</th>
                <th class="col--num">Entregada</th>
                <th class="col--num">Pendiente</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in detail.items" :key="item.id">
                <td class="col--center">
                  <span class="req-check" :class="supplyClass(item)" :title="supplyLabel(item)" role="img" :aria-label="supplyLabel(item)"></span>
                </td>
                <td>{{ item.part_barcode }}</td>
                <td class="cell-strong">{{ item.part_name }}</td>
                <td class="col--num">{{ formatNumber(item.quantity) }} {{ item.part_unit_of_measure || '' }}</td>
                <td class="col--num">{{ formatNumber(item.delivered_quantity) }}</td>
                <td class="col--num cell-strong">{{ formatNumber(item.remaining_quantity) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="detail.deliveries && detail.deliveries.length" class="deliveries">
            <h4 class="section-title">Entregas</h4>
            <ul>
              <li v-for="delivery in detail.deliveries" :key="delivery.id">
                <strong>{{ delivery.voucher_number }}</strong>
                <span>{{ formatDateTime(delivery.created_at) }}</span>
              </li>
            </ul>
          </div>
        </div>
        <div class="modal-actions">
          <button class="action-button" @click="detail = null">Cerrar</button>
          <button class="action-button" @click="printTicketCurrent">Imprimir ticket</button>
          <button class="action-button action-primary" @click="printCurrent">Imprimir</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from '@/utils/warehouseSwal'
import { printRequisition } from '@/utils/printRequisition'
import { printRequisitionTicket } from '@/utils/printTicket'
import warehouseTable from '@/mixins/warehouseTable'

export default {
  name: 'WarehouseRequisitions',
  mixins: [warehouseTable],
  data() {
    return {
      requisitions: [],
      loading: false,
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 20,
      filters: this.emptyFilters(),
      sortState: { key: '', dir: 'asc' },
      detail: null,
    }
  },
  mounted() {
    this.fetchRequisitions(1)
  },
  methods: {
    emptyFilters() {
      return {
        requisition_number: '',
        order_number: '',
        part: '',
        status: '',
        start_date: '',
        end_date: '',
      }
    },
    backMainPanel() {
      this.$router.push('/main-panel')
    },
    backWarehouse() {
      this.$router.push('/warehouse')
    },
    formatNumber(value) {
      const num = Number(value)
      if (Number.isNaN(num)) return 'N/A'
      return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDateTime(value) {
      if (!value) return 'N/A'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'N/A'
      return date.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
    },
    statusClass(status) {
      if (status === 'fulfilled') return 'status-pill--ok'
      if (status === 'partial') return 'status-pill--warn'
      return 'status-pill--danger'
    },
    supplyClass(item) {
      if (item.is_fulfilled) return 'req-check--done'
      if (Number(item.delivered_quantity) > 0) return 'req-check--partial'
      return ''
    },
    supplyLabel(item) {
      if (item.is_fulfilled) return 'Surtida'
      if (Number(item.delivered_quantity) > 0) return 'Parcial'
      return 'Pendiente'
    },
    progressPercent(requisition) {
      const total = Number(requisition.items_total) || 0
      if (!total) return 0
      const done = Number(requisition.items_fulfilled) || 0
      return Math.min(100, Math.round((done / total) * 100))
    },
    progressClass(requisition) {
      const percent = this.progressPercent(requisition)
      if (percent === 0) return 'progress-cell__bar--empty'
      if (percent < 100) return 'progress-cell__bar--partial'
      return ''
    },
    toggleSort(key) {
      if (this.sortState.key === key) {
        this.sortState.dir = this.sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortState = { key, dir: 'asc' }
      }
      this.fetchRequisitions(1)
    },
    applyFilters() {
      this.fetchRequisitions(1)
    },
    clearFilters() {
      this.filters = this.emptyFilters()
      this.fetchRequisitions(1)
    },
    goToPage(pageNumber) {
      if (typeof pageNumber !== 'number') return
      if (pageNumber < 1 || pageNumber > this.totalPages || pageNumber === this.currentPage) return
      this.fetchRequisitions(pageNumber)
    },
    async fetchRequisitions(page) {
      const apiDirectory = process.env.API_DIRECTORY
      this.loading = true
      try {
        let orderingParam = ''
        if (this.sortState.key) {
          orderingParam = `&ordering=${this.sortState.dir === 'desc' ? '-' : ''}${this.sortState.key}`
        }
        const response = await axios.post(
          `${apiDirectory}/warehouse/get-requisitions-filtered?page=${page}&page_size=${this.pageSize}${orderingParam}`,
          this.filters,
          { headers: { 'Content-Type': 'application/json' } },
        )
        this.requisitions = response.data.results || []
        this.totalCount = response.data.count || 0
        this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize))
        this.currentPage = page
      } catch (error) {
        console.error('Failed to fetch requisitions:', error)
        Swal.fire('Error', 'No se pudieron cargar las requisiciones.', 'error')
      } finally {
        this.loading = false
      }
    },
    openDetail(requisition) {
      this.detail = requisition
    },
    printCurrent() {
      printRequisition(this.detail)
    },
    printTicketCurrent() {
      printRequisitionTicket(this.detail)
    },
  },
}
</script>

<style scoped src="@/styles/warehouse.css"></style>
